import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { TranscriptEditorChangeType } from '../types';
import { Paragraph } from '../models/Paragraph';
import { TextElement } from '../models/TranscriptElement';

/**
 * Command to delete a transcript element
 * 
 * This command removes an element from a paragraph. If the paragraph
 * becomes empty of text elements as a result, it may also delete the paragraph.
 */
export class DeleteElementCommand extends AbstractTranscriptEditorCommand {
  /** ID of the segment containing the element */
  private segmentId: string;
  
  /** ID of the paragraph containing the element */
  private paragraphId: string;
  
  /** ID of the element to delete */
  private elementId: string;
  
  /** User ID for operation tracking */
  private userId: string;
  
  /** Original element data for undo operations */
  private originalElement: any = null;
  
  /** Original paragraph elements for undo operations */
  private originalParagraphElements: any[] = [];
  
  /** Flag indicating if the paragraph was deleted */
  private paragraphWasDeleted: boolean = false;
  
  /**
   * Creates a new DeleteElementCommand
   * 
   * @param editor Reference to the transcript editor core
   * @param segmentId ID of the segment containing the element
   * @param paragraphId ID of the paragraph containing the element
   * @param elementId ID of the element to delete
   * @param userId ID of the user making the change
   */
  constructor(
    editor: TranscriptEditorCore,
    segmentId: string,
    paragraphId: string,
    elementId: string,
    userId: string
  ) {
    super(`Delete element ${elementId}`, editor);
    
    this.segmentId = segmentId;
    this.paragraphId = paragraphId;
    this.elementId = elementId;
    this.userId = userId;
    
    // Store metadata for debugging and tracking
    this.setMetadata('segmentId', segmentId);
    this.setMetadata('paragraphId', paragraphId);
    this.setMetadata('elementId', elementId);
    this.setMetadata('userId', userId);
  }
  
  /**
   * Execute the command
   * 
   * @returns Promise resolving to true if successful
   */
  async execute(): Promise<boolean> {
    try {
      // Get the container
      const container = this.editor.getContainer();
      if (!container) {
        return false;
      }
      
      // Get the segment
      const segment = container.getSegment(this.segmentId);
      if (!segment) {
        return false;
      }
      
      // Get the paragraph
      const paragraph = segment.getParagraph(this.paragraphId);
      if (!paragraph) {
        return false;
      }
      
      // Get the element
      const element = paragraph.getElement(this.elementId);
      if (!element) {
        return false;
      }
      
      // Store the original element and paragraph elements for undo
      this.originalElement = { ...element };
      this.originalParagraphElements = [...paragraph.elements];
      
      // Remove the element from the paragraph
      const removed = paragraph.removeElement(this.elementId);
      if (!removed) {
        return false;
      }
      
      // Record the operation
      this.editor.recordOperation({
        type: 'element_delete',
        segmentId: this.segmentId,
        paragraphId: this.paragraphId,
        elementId: this.elementId,
        data: {
          element: this.originalElement
        },
        userId: this.userId,
        timestamp: Date.now()
      });
      
      // Notify observers of the change
      this.editor.notifyObservers(TranscriptEditorChangeType.ELEMENT_DELETED, {
        segment,
        paragraph,
        elementId: this.elementId,
        element: this.originalElement
      });
      
      // Check if paragraph is now empty of text elements
      const hasTextElements = paragraph.elements.some(e => e.type === 'text');
      if (!hasTextElements || paragraph.elements.length === 0) {
        // Paragraph has no more text elements, delete it
        this.paragraphWasDeleted = true;
        this.editor.deleteParagraph(this.segmentId, this.paragraphId, this.userId);
      } else {
        // Update paragraph timing based on remaining elements
        const textElements = paragraph.elements.filter(e => e.type === 'text') as TextElement[];
        if (textElements.length > 0) {
          // Find earliest start time and latest end time
          const startTimes = textElements
            .map(e => e.startTime)
            .filter(t => t !== undefined) as number[];
          
          const endTimes = textElements
            .map(e => e.endTime)
            .filter(t => t !== undefined) as number[];
          
          if (startTimes.length > 0) {
            paragraph.startTime = Math.min(...startTimes);
          }
          
          if (endTimes.length > 0) {
            paragraph.endTime = Math.max(...endTimes);
          }
        }
      }
      
      // Set metadata about the deletion
      this.setMetadata('elementType', this.originalElement.type);
      this.setMetadata('elementValue', this.originalElement.value);
      this.setMetadata('paragraphWasDeleted', this.paragraphWasDeleted);
      
      return true;
    } catch (error) {
      console.error('Error executing DeleteElementCommand:', error);
      
      return false;
    }
  }
  
  /**
   * Undo the command
   * 
   * @returns Promise resolving to true if successful
   */
  async undo(): Promise<boolean> {
    
    try {
      // Get the container
      const container = this.editor.getContainer();
      if (!container) {
        return false;
      }
      
      // Get the segment
      const segment = container.getSegment(this.segmentId);
      if (!segment) {
        return false;
      }
      
      // If the paragraph was deleted, we need to restore it first
      let paragraph = segment.getParagraph(this.paragraphId);
      if (!paragraph && this.paragraphWasDeleted) {
        // Create a new paragraph with the original ID
        const ParagraphClass = this.editor.getParagraphClass();
        const newParagraph = new ParagraphClass(
          this.paragraphId,
          (this.originalElement as TextElement).startTime || 0,
          (this.originalElement as TextElement).endTime || 0,
          [],
          this.segmentId
        );
        
        // Add to segment
        segment.addParagraph(newParagraph);
        paragraph = newParagraph;
      }
      
      if (!paragraph) {
        return false;
      }
      
      // Add the element back to the paragraph
      paragraph.addElement(this.originalElement);
      
      // Notify observers of the undo
      this.editor.notifyObservers(TranscriptEditorChangeType.ELEMENT_UPDATED, {
        segment,
        paragraph,
        element: this.originalElement,
        restored: true
      });
      
      return true;
    } catch (error) {
      console.error('Error undoing DeleteElementCommand:', error);
      
      return false;
    }
  }
}